import { useState, useEffect} from 'react';
import { Form, Row, Col, Button, Card, Image } from 'react-bootstrap';
import { validateURL} from './utils/validation';
import icValidationError from './assets/img/ic-danger.svg';
import icLink from './assets/img/ic-link-square.svg';
import icPhone from './assets/img/ic-smart-phone.svg';
import icHome from './assets/img/ic-home.svg';

const CityOfficeDesk = ({ formData, handleChange, errors, handleGeneralSubmit, isEditPage, isReviewPage, setChildHasErrors, onShowErrorMessage }) => {
    const [isEditing, setIsEditing] = useState(!isEditPage); // If adding, fields start enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // Store initial values
    const [localErrors, setLocalErrors] = useState({}); // ✅ Renamed errors state to avoid conflict

    const [officeUrl, setOfficeUrl] = useState(formData.officeUrl || '');

    const [workingHours, setWorkingHours] = useState(
        formData.workingHours || [{ fromDay: '', toDay: '', fromTime: '', toTime: '' }]
      );

    useEffect(() => {
        setOfficeUrl(formData.officeUrl || '');
    }, [formData.officeUrl]);

    useEffect(() => {
        const has = !!Object.values(localErrors || {}).some(Boolean);
        setChildHasErrors?.(has);
    }, [localErrors, setChildHasErrors]);

    // ✅ normalize + validate
    const normalizeUrl = (raw) => {
        const s = (raw || "").trim();
        if (!s) return null;

        // If no scheme, default to https://
        const withScheme = /^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(s) ? s : `https://${s}`;

        try {
            const u = new URL(withScheme);
            // Only allow http/https
            if (!["http:", "https:"].includes(u.protocol)) return null;
            return u.toString();
        } catch {
            return null;
        }
    };

    // ✅ normalize phone helper
    const normalizePhone = (raw) => {
        if (!raw) return '';
        let s = raw.trim();

        // keep only digits and +
        s = s.replace(/[^\d+]/g, '');

        // allow + only as first char
        if (s.includes('+')) {
            s = '+' + s.replace(/\+/g, '');
        }

        return s;
    };

    const isValidPhone = (phone) =>
        /^(\+?\d{6,15})$/.test(phone);

    const handlePhoneChange = (e) => {
        const cleaned = normalizePhone(e.target.value);

        if (cleaned && !isValidPhone(cleaned)) {
            setLocalErrors(prev => ({ ...prev, phone: 'Please enter a valid phone number.' }));
            setChildHasErrors?.(true); // ✅ invalid
            handleChange({ target: { name: 'phone', value: cleaned } });
            return;
        }

        setLocalErrors(prev => ({ ...prev, phone: '' }));
        setChildHasErrors?.(false);   // ✅ valid (or empty)
        handleChange({ target: { name: 'phone', value: cleaned } });
    };

    const safeHref = normalizeUrl(officeUrl);

    const handleOfficeURLChange = (e) => {
        const { value } = e.target;
        setOfficeUrl(value);
    
        clearTimeout(window.officeUrlValidationTimeout);
        window.officeUrlValidationTimeout = setTimeout(() => {
            handleChange({ target: { name: 'officeUrl', value } });
            if (value && !validateURL(value)) {
                setLocalErrors(prev => ({ ...prev, officeUrl: 'Please enter a valid URL.' }));
            } else {
                setLocalErrors(prev => ({ ...prev, officeUrl: '' }));
            }
        }, 800);
    };
    
    // Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // Store previous values
    };

    // Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();

        // ✅ stop debounce + validate NOW
        if (window.officeUrlValidationTimeout) clearTimeout(window.officeUrlValidationTimeout);

        const trimmedUrl = (officeUrl || '').trim();

        // ✅ required on edit/save (since field is marked *)
        if (!trimmedUrl) {
            setLocalErrors(prev => ({ ...prev, officeUrl: 'Citizen office desk URL is required.' }));
            setChildHasErrors?.(true);
            onShowErrorMessage?.(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return; // ✅ BLOCK save
        }

        // ✅ must be valid
        if (!validateURL(trimmedUrl)) {
            setLocalErrors(prev => ({ ...prev, officeUrl: 'Please enter a valid URL.' }));
            setChildHasErrors?.(true);
            onShowErrorMessage?.(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return; // ✅ BLOCK save
        }

        // ✅ URL is valid → push to parent immediately
        if (trimmedUrl !== (formData.officeUrl || '')) {
            setLocalErrors(prev => ({ ...prev, officeUrl: '' }));
            setChildHasErrors?.(false);
            handleChange({ target: { name: 'officeUrl', value: trimmedUrl } });
        }

        if (typeof handleGeneralSubmit === 'function') {
            const ok = await handleGeneralSubmit();
            if (!ok) return;
            setIsEditing(false);
        } else {
            console.error('Error: handleGeneralSubmit is not a function.');
        }
    };

    // Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);
        handleChange({ target: { name: 'formData', value: tempFormData } }); // Restore previous values
    };
    
    const generateTimeOptions = () => {
        const options = [];

        for (let hour = 0; hour < 24; hour++) {
            for (let minute of [0, 30]) {
                const isPM = hour >= 12;
                const displayHour = hour % 12 === 0 ? 12 : hour % 12;

                const timeLabel = `${String(displayHour).padStart(2, '0')}:${minute === 0 ? '00' : '30'} ${isPM ? 'PM' : 'AM'}`;
                const timeValue = `${String(hour).padStart(2, '0')}:${minute === 0 ? '00' : '30'}`;

                options.push(
                    <option key={timeValue} value={timeValue}>
                        {timeLabel}
                    </option>
                );
            }
        }

        return options;
    };

    const handleWorkingHoursChange = (index, field, value) => {
        const updated = [...workingHours];
        updated[index][field] = value;
        setWorkingHours(updated);
        handleChange({ target: { name: 'workingHours', value: updated } });
    };

    const addMoreDays = () => {
        const updated = [...workingHours, { fromDay: '', toDay: '', fromTime: '', toTime: '' }];
        setWorkingHours(updated);
        handleChange({ target: { name: 'workingHours', value: updated } });
    };

    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    {/* Title and Edit Button */}
                    <Row className="align-items-center">
                        <Col>
                            {isEditPage && (
                                <div className="float-end">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3><span className="item-number">2. </span>Citizen office desk</h3>
                            <hr />
                        </Col>
                    </Row>

                    {isEditing ? (
                        <Row>
                            {/* Form Fields */}
                            <Col md={12}>
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <Form.Group className="validated" controlId="officeUrl">
                                            <Form.Label>Citizen office desk URL <span>*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="officeUrl"
                                                value={officeUrl}
                                                onChange={handleOfficeURLChange}
                                                isInvalid={!!localErrors.officeUrl || !!errors.officeUrl} // ✅ Check both
                                                disabled={!isEditing}
                                                required
                                            />
                                            {/* ✅ Custom error icon + tooltip */}
                                            {(!!localErrors.officeUrl || !!errors.officeUrl) && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={localErrors.officeUrl || errors.officeUrl}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">
                                                        {localErrors.officeUrl || errors.officeUrl}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="validated" controlId="address">
                                            <Form.Label>Address <span>*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="address"
                                                value={formData.address}
                                                onChange={handleChange}
                                                isInvalid={!!errors.address}
                                                required
                                                disabled={!isEditing}
                                            />
                                            {/* ✅ Custom error icon + tooltip */}
                                            {!!errors.address && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.address}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">{errors.address}</div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="validated" controlId="phone">
                                            <Form.Label>Phone <span>*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handlePhoneChange}
                                                isInvalid={!!localErrors.phone || !!errors.phone}
                                                required
                                                disabled={!isEditing}
                                            />
                                            {/* ✅ Custom error icon + tooltip */}
                                            {!!localErrors.phone && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img src={icValidationError} alt="error" />
                                                    <div className="custom-tooltip">{localErrors.phone}</div>
                                                </div>
                                            )}
                                            
                                            {!!errors.phone && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.phone}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">{errors.phone}</div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {workingHours.map((entry, index) => (
                                    <Row key={index} className="mb-4">
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label>From (Day)</Form.Label>
                                                <Form.Select
                                                    value={entry.fromDay}
                                                    onChange={(e) => handleWorkingHoursChange(index, 'fromDay', e.target.value)}
                                                    disabled={!isEditing}
                                                >
                                                    <option value="">Select a day</option>
                                                    <option value="Monday">Monday</option>
                                                    <option value="Tuesday">Tuesday</option>
                                                    <option value="Wednesday">Wednesday</option>
                                                    <option value="Thursday">Thursday</option>
                                                    <option value="Friday">Friday</option>
                                                    <option value="Saturday">Saturday</option>
                                                    <option value="Sunday">Sunday</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label>To (Day)</Form.Label>
                                                <Form.Select
                                                    value={entry.toDay}
                                                    onChange={(e) => handleWorkingHoursChange(index, 'toDay', e.target.value)}
                                                    disabled={!isEditing}
                                                >
                                                    <option value="">Select a day</option>
                                                    <option value="Monday">Monday</option>
                                                    <option value="Tuesday">Tuesday</option>
                                                    <option value="Wednesday">Wednesday</option>
                                                    <option value="Thursday">Thursday</option>
                                                    <option value="Friday">Friday</option>
                                                    <option value="Saturday">Saturday</option>
                                                    <option value="Sunday">Sunday</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label>From (Time)</Form.Label>
                                                <Form.Select
                                                    value={entry.fromTime}
                                                    onChange={(e) => handleWorkingHoursChange(index, 'fromTime', e.target.value)}
                                                    disabled={!isEditing}
                                                >
                                                    <option value="">Select time</option>
                                                    {generateTimeOptions()}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label>To (Time)</Form.Label>
                                                <Form.Select
                                                    value={entry.toTime}
                                                    onChange={(e) => handleWorkingHoursChange(index, 'toTime', e.target.value)}
                                                    disabled={!isEditing}
                                                >
                                                    <option value="">Select time</option>
                                                    {generateTimeOptions()}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                ))}
                                {isEditing &&
                                    <Row>
                                        <Col>
                                            <Col md={12} className="text-center">
                                                <Button
                                                    variant="outline-secondary"
                                                    className="btn-sm"
                                                    onClick={addMoreDays}
                                                    disabled={!isEditing}
                                                >
                                                    Add More Days
                                                </Button>
                                            </Col>
                                        </Col>
                                    </Row>
                                }
                                {isReviewPage && (
                                    <Row>
                                        <Col md={6}>
                                            <p>Under Review</p> {/* ✅ Show only in Review Page */}
                                        </Col>
                                    </Row>
                                )}
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icLink} alt="Citizen office desk URL" />
                                        </span>
                                        <span className="text">
                                            Citizen office desk URL
                                            <br />
                                            <strong>
                                                {safeHref ? (
                                                    <a href={safeHref} target="_blank" rel="noopener noreferrer">
                                                        {formData.officeUrl}
                                                    </a>
                                                ) : (
                                                    "N/A"
                                                )}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icHome} alt="Address" />
                                        </span>
                                        <span className="text">
                                            Address
                                            <br />
                                            <strong>
                                                {formData.address || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icLink} alt="Opening hours" />
                                        </span>
                                        <span className="text">
                                            Opening hours
                                            <br />
                                            <strong>
                                                {(formData.workingHours?.length ? formData.workingHours : workingHours || [])
                                                    .filter(h => h) // ✅ guard against nulls
                                                    .map((h, i) => (
                                                    <span key={i}>
                                                        {/* Day range */}
                                                        {(h.fromDay || '-')}{h.toDay ? `–${h.toDay}` : ''}:&nbsp;
                                                        {/* Time range */}
                                                        {(h.fromTime || '-')}{h.toTime ? `–${h.toTime}` : ''}
                                                        {i < ((formData.workingHours?.length ? formData.workingHours : workingHours || []).length - 1) ? <><br /></> : null}
                                                    </span>
                                                    ))
                                                || "N/A"}
                                             </strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icPhone} alt="Phone" />
                                        </span>
                                        <span className="text">
                                            Phone
                                            <br />
                                            <strong>
                                                {formData.phone || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </Card.Body>
            </Card>    
        </>
    );
};

export default CityOfficeDesk;
